/** Converts multiple intersection types into a flattened record type
 *
 * E.g.:
 * ```ts
 *   Collapsed<{a: any} & {b: any}> => {a: any; b: any}
 * ```
 */
export type Collapsed<R> = R extends object
  ? { [RR in keyof R]: R[RR] }
  : never;

/** Ensures that a passed object does _not_ have properties */
// cf. https://www.totaltypescript.com/tips/derive-a-union-type-from-an-object
export type Ban<T extends object, P extends string> = T extends {
  [PP in P]: { [PPP in PP]: any };
}[P]
  ? never
  : T;

/** Ensures that all object properties and any nested properties are optional */
export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends object ? RecursivePartial<T[P]> : T[P];
};

/** Distributes pick over a union type
 * so if type A = B | C | D , Pick<A, "key"> = Pick<B, "key"> | Pick<C, "key"> | Pick<D, "key">
 */
export type DistributedPick<T, K extends keyof T> = T extends any
  ? Pick<T, K>
  : never;

/** Distributes omit over a union type
 * so if type A = B | C | D , Omit<A, "key"> = Omit<B, "key"> | Omit<C, "key"> | Omit<D, "key">
 */
export type DistributedOmit<T, K extends keyof T> = T extends any
  ? Omit<T, K>
  : never;

/** provides a union of all keys of a union type
 * https://stackoverflow.com/questions/49401866/all-possible-keys-of-an-union-type*/
export type DistributedKeys<T> = T extends T ? keyof T : never;

/** Recursively treats all properties as readonly */
export type RecursiveReadonly<T> = T extends (...args: any[]) => any
  ? // If T is a function, need to keep its "call" interface
    T & { readonly [K in keyof T]: RecursiveReadonly<T[K]> }
  : // Note that this 'object' handles arrays
    T extends object
    ? { readonly [K in keyof T]: RecursiveReadonly<T[K]> }
    : T;

/** Types a value as recursive readonly
 *
 * Note that this only alters types, it does not perform any sort of freeze nor deep freeze on the value.
 */
export const recursiveReadonly = <T>(value: T): RecursiveReadonly<T> =>
  value as any;

/** Extracts the type of array elements */
export type Unpacked<T> = T extends (infer U)[] ? U : T;

/** Distributes type unions */
export type Distribute<A> = A extends unknown ? A : never;

/** `true` if two types are equal, `false` otherwise */
export type IsEqual<A, B> =
  Distribute<A> extends Distribute<B>
    ? Distribute<B> extends Distribute<A>
      ? true
      : false
    : false;

/** Fails to compile unless `T` is exactly `true`; use for compile-time assertions. */
export type Expect<T extends true> = T;

type First<T> = T extends [infer K, infer _V] ? K : never;
type Second<T> = T extends [infer _K, infer V] ? V : never;
/** Converts an array of pairs to an object type that would result from calling `fromEntries` on the array
 *
 * Example:
 *
 *   type Foo = [["a", "1"], ["b", 2]];
 *   type Bar = RecordFromArray<Foo> // { a: "1", b: 2 }
 */
export type RecordFromArray<A extends (readonly [any, any])[]> = {
  [I in keyof A as First<A[I]> & string]: Second<A[Exclude<I, number>]>;
};

/** The resolved value of a promise */
export type Resolved<P extends Promise<any>> =
  P extends Promise<infer T> ? T : never;

export type RequireKeys<T, K extends keyof T> = T & Required<Pick<T, K>>;

export type NonEmpty<T extends any[]> = [T[number], ...T];
